import { type ProjectCode } from '@constants'
import { type ValueOf } from '@dni-types/utilities/value-of'

export const FAST_ORDER_ROW_STATUS = {
  PASSED: 'passed',
  ERROR: 'error',
} as const
export type FastOrderRowStatusType = ValueOf<typeof FAST_ORDER_ROW_STATUS>

export const FAST_ORDER_ROW_ERROR_CODE = {
  BLANK_ROW: 'blank_row',
  MISSING_FIELD: 'missing_field',
  INVALID_QUANTITY: 'invalid_quantity',
  PRODUCT_NOT_FOUND: 'product_not_found',
  AMBIGUOUS_MATCH: 'ambiguous_match',
  ORDER_LIMIT_EXCEEDED: 'order_limit_exceeded',
  BRAND_NOT_PERMITTED: 'brand_not_permitted',
} as const
export type FastOrderRowErrorCodeType = ValueOf<typeof FAST_ORDER_ROW_ERROR_CODE>

export const FAST_ORDER_ROW_FIELD = {
  BRAND: 'brand',
  REAL_SKU: 'real_sku',
  QUANTITY: 'quantity',
} as const
export type FastOrderRowFieldType = ValueOf<typeof FAST_ORDER_ROW_FIELD>

export const FAST_ORDER_FILE_ERROR_CODE = {
  UNPARSEABLE: 'unparseable',
  EMPTY_FILE: 'empty_file',
  HEADER_ONLY: 'header_only',
  MISSING_HEADER: 'missing_header',
  ROW_LIMIT_EXCEEDED: 'row_limit_exceeded',
} as const
export type FastOrderFileErrorCodeType = ValueOf<typeof FAST_ORDER_FILE_ERROR_CODE>

export const FAST_ORDER_ITEM_CHECK_STATUS = {
  PASSED: 'passed',
  OUT_OF_STOCK: 'out_of_stock',
  INVALID_QUANTITY: 'invalid_quantity',
  STOCK_UNAVAILABLE: 'stock_unavailable',
  PRICING_UNAVAILABLE: 'pricing_unavailable',
} as const
export type FastOrderItemCheckStatusType = ValueOf<typeof FAST_ORDER_ITEM_CHECK_STATUS>

export const FAST_ORDER_SUBMISSION_ERROR_CODE = {
  FINAL_CHECK_FAILED: 'final_check_failed',
  SUBMISSION_IN_PROGRESS: 'submission_in_progress',
  BATCH_WRITE_FAILED: 'batch_write_failed',
} as const
export type FastOrderSubmissionErrorCodeType = ValueOf<typeof FAST_ORDER_SUBMISSION_ERROR_CODE>

export const FAST_ORDER_REJECT_REASON = {
  OUT_OF_STOCK: 'out_of_stock',
  ORDER_LIMIT_EXCEEDED: 'order_limit_exceeded',
  PRODUCT_INACTIVE: 'product_inactive',
  OWNERSHIP_CHANGED: 'ownership_changed',
  IDENTITY_INVALID: 'identity_invalid',
  DATA_UNAVAILABLE: 'data_unavailable',
} as const
export type FastOrderRejectReasonType = ValueOf<typeof FAST_ORDER_REJECT_REASON>

export const FAST_ORDER_RETURN_TO_STEP = {
  STORY_002: 'story_002',
  STORY_001: 'story_001',
  ENTRY: 'entry',
  STAY: 'stay',
} as const
export type FastOrderReturnToStepType = ValueOf<typeof FAST_ORDER_RETURN_TO_STEP>

export interface FastOrderBrand {
  project_code: ProjectCode
  brand_name: string
  is_default: boolean
  display_order?: number
}

export interface FastOrderCsvValidationRow {
  /** CSV 實體列號，含標題列（資料首列為 2） */
  row_number: number
  brand_input: string
  real_sku_input: string
  quantity_input: string
  status: FastOrderRowStatusType
  error_code?: FastOrderRowErrorCodeType
  error_field?: FastOrderRowFieldType
  error_message?: string
  related_row_numbers?: number[]
  matched_product_name?: string | null
  cart_project_code?: ProjectCode | null
}

export interface FastOrderProductSnapshot {
  sku: string
  variation_id: number
  parent_product_id: number
  parent_product_name: string
  display_name: string
  spec_name?: string | null
  image_url?: string | null
  unit: string
  unit_price: number
}

export interface FastOrderItem {
  project_code: ProjectCode
  real_sku: string
  quantity: number
  source_row_numbers: number[]
  product: FastOrderProductSnapshot
}

export interface FastOrderCsvValidationResult {
  passed: boolean
  total_rows: number
  rows: FastOrderCsvValidationRow[]
  /** 僅 passed 為 true 時存在 */
  items?: FastOrderItem[]
}

export interface FastOrderFileError {
  file_error_code: FastOrderFileErrorCodeType
  message?: string
}

export interface FastOrderItemInput {
  project_code: ProjectCode
  real_sku: string
  quantity: number
}

export interface FastOrderItemCheckRow extends FastOrderItemInput {
  status: FastOrderItemCheckStatusType
  remaining_quantity: number | null
  unit_price?: number | null
  amount?: number | null
  error_message?: string | null
}

export interface FastOrderItemsCheckResult {
  can_submit: boolean
  items: FastOrderItemCheckRow[]
}

export interface FastOrderSubmissionItem extends FastOrderItemInput {
  source_row_numbers: number[]
}

export interface FastOrderSubmissionBrandResult {
  project_code: ProjectCode
  cart_uuid: string
  items: {
    real_sku: string
    quantity: number
    updated_quantity: number
  }[]
}

export interface FastOrderSubmissionResult {
  submission_id: string
  brands: FastOrderSubmissionBrandResult[]
  cleared_project_codes?: ProjectCode[]
}

export interface FastOrderSubmissionRejectedError {
  project_code: ProjectCode
  real_sku: string
  source_row_numbers: number[]
  reason: FastOrderRejectReasonType
  latest_remaining_quantity?: number | null
  return_to_step: FastOrderReturnToStepType
}

export interface FastOrderSubmissionRejected {
  error_code: FastOrderSubmissionErrorCodeType
  message?: string
  errors?: FastOrderSubmissionRejectedError[]
}
